// Import prior bookings (GL-21). AM and Owner, one company.
// Upload uses the admin mint route and the signed PUT (GL-14 / GL-28).
// Amounts render only in the bookings list. Memo text is never stored and
// is not rendered. historicalPullComplete null is "not known yet".

import {
  AdminApiError,
  type AdminClient,
  type OpenAccountRow,
  type PriorAccountMapGet,
  type PriorAccountMapRow,
  type PriorBooking,
  type PriorCoverage,
  type PriorCoverageBank,
  type PriorImportDetail,
  type PriorImportFormat,
  type PriorImportRecord,
  type PriorImportStatus,
  type PriorMapKind,
} from "../api.js";
import { amVisibleDisplayText } from "../../../ingest-worker/src/am_visible_text.js";
import { csvField } from "../csv_field.js";
import { clear, h, safeText } from "../dom.js";
import { runAnnouncedAction } from "../ui/announced-action.js";
import { settleReads } from "../read-cache.js";
import { disclosure, focusAfterWrite } from "../ui/components.js";
import {
  accountLabelSet,
  countOrMoreText,
  type AccountLabelContext,
} from "../ui/display-words.js";
import * as face from "../ui/face.js";
import { bankConnection, readerRole } from "../ui/facts.js";
import { AuthoredRefusal, failureCopy, outcomeSentence } from "../ui/failure.js";
import { formatCents } from "../ui/money.js";
import { lastHealth } from "../ui/nav-bus.js";
import { navLabel, sentence } from "../ui/sentences.js";
import { forgetPossiblySent, runWrite } from "../ui/write.js";
import type { CompanyHealth, CompanySlug, StaffRole } from "../types.js";

/** Mirrors the Worker prior-import body cap (10 MiB). */
export const MAX_PRIOR_IMPORT_BYTES = 10 * 1024 * 1024;

const NOT_FOUND_AFTER_DAYS = 14;
const MAX_PAGES = 20;
const DEFAULT_POLL_MS = 2_000;
const DEFAULT_POLL_MAX_MS = 20_000;

const ERROR_COPY: Readonly<Record<string, string>> = {
  company_identity_missing:
    "This company's type or feature packs are missing, so the import did not run.",
  medical_company: "This company is not eligible for a prior-software import.",
  download_failed: "The import file could not be read back for parsing.",
  unknown_format: "The file does not match Wave, Buildium, or the GV template.",
  too_large: "The file is larger than 10 MB.",
  bank_map_missing: "A bank name in the file has no mapping yet.",
  not_cash: "The file is not a cash-basis export.",
  buildium_requires_bank_file:
    "A Buildium import needs its bank file as well as the general ledger.",
  refused_extension: "Only CSV and XLSX files can be imported.",
  split_unbalanced: "A split in the file does not add up, so those rows were not stored.",
  ambiguous_pairing:
    "Some rows could not be paired. The count is recorded; those rows were not stored.",
  parse_invalid: "The parsed rows were rejected.",
  prior_import_parse_invalid: "The parsed rows were rejected.",
  pending_list_unavailable: "The nightly parse could not list waiting imports.",
};

export type BookingFilter =
  | "all"
  | "matched-booked"
  | "queued"
  | "ambiguous"
  | "unmatched"
  | "excluded"
  | "changed-after-booking";

export interface PriorImportPageOptions {
  client: AdminClient;
  company: CompanySlug;
  root: HTMLElement;
  /** The role the shell already knows (decision 10: unknown reads as the AM). */
  role?: StaffRole;
  isCurrent?: () => boolean;
  now?: () => Date;
  pollIntervalMs?: number;
  pollMaxMs?: number;
  sleep?: (ms: number) => Promise<void>;
  /**
   * The shell's company names (slug -> name), from which the page builds its
   * ONE account-label context (rd13 L1): the Map-value picker names an
   * account, so a between-companies one reads "Due from <company>".
   */
  companyLabels?: ReadonlyMap<CompanySlug, string>;
  /** The shell's ledger segment to company name read (`face.companySegmentNames`, rd13 L1). */
  companySegments?: ReadonlyMap<string, string>;
}

export interface CoverageLine {
  tone: "gap" | "info";
  text: string;
}

/*
 * A bad choice made on this page is a `runWrite` gate reason, not a thrown
 * error: nothing is sent, so it can never read as a save that may have been
 * lost.
 */

interface Loaded {
  coverage: PriorCoverage;
  map: PriorAccountMapGet;
  openAccounts: readonly string[];
  locations: readonly string[];
  /** False when the chart read failed: the list is unknown, not empty. */
  locationsKnown: boolean;
  imports: readonly PriorImportRecord[];
  bookings: readonly PriorBooking[];
  truncated: boolean;
}

const FILTERS: readonly { id: BookingFilter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "matched-booked", label: "Matched / booked" },
  { id: "queued", label: "Queued" },
  { id: "ambiguous", label: "Ambiguous" },
  { id: "unmatched", label: "Unmatched" },
  { id: "excluded", label: "Excluded" },
  { id: "changed-after-booking", label: "Changed after booking" },
];

export function fileKindOfName(name: string): "csv" | "xlsx" | null {
  const lower = name.toLowerCase();
  if (lower.endsWith(".csv")) return "csv";
  if (lower.endsWith(".xlsx")) return "xlsx";
  return null;
}

export function isPriorImportFormat(value: string): value is PriorImportFormat {
  return value === "gv-template" || value === "wave" || value === "buildium";
}

/** Null when the file can be uploaded. Both the accepting and refusing cases. */
export function uploadRefusal(file: { name: string; size: number }, format: string): string | null {
  if (!isPriorImportFormat(format)) {
    return "Choose Wave, Buildium, or the GV template before uploading.";
  }
  if (fileKindOfName(file.name) === null) {
    return "Only a CSV or XLSX file can be uploaded.";
  }
  if (file.size > MAX_PRIOR_IMPORT_BYTES) {
    return "CSV and XLSX files must be 10 MB or smaller.";
  }
  return null;
}

export async function sha256HexOfBlob(blob: Blob): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", await blob.arrayBuffer());
  return Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
}

export function priorImportErrorCopy(code: string | null): string {
  if (code === null || code === "") return "The import failed.";
  const known = ERROR_COPY[code];
  if (known !== undefined) return known;
  if (/^[a-z][a-z0-9_]{0,63}$/.test(code)) return `The import failed (${code}).`;
  return "The import failed.";
}

/**
 * What a failed prior-import request says on the face.
 *
 * round-11 item 24: the words come from the one failure table
 * (`failureCopy`), never from the server's own message. A 422 keeps this
 * page's authored sentence (round 11, R7-01): it names the one thing the person
 * can fix, which no status table can.
 */
export function priorImportApiFailure(
  error: unknown,
  role: StaffRole = "account_manager",
): string {
  if (error instanceof AdminApiError && error.status === 422) {
    return "That account is not an open GV account for this company. Pick an account from the list.";
  }
  return failureCopy(error, { role, context: "read" }).sentence;
}

/**
 * The scrubbed detail for the "Show details" body, including the request id a
 * person quotes to support. Never a sentence on the face.
 */
export function priorImportFailureDetail(error: unknown): string | null {
  const copy = failureCopy(error, { role: "account_manager", context: "read" });
  const id = requestIdDetail(error);
  if (copy.detail === null) return id;
  if (id === null) return copy.detail;
  return `${copy.detail} ${id}`;
}

/** The request id, in words, for the disclosure only. */
function requestIdDetail(error: unknown): string | null {
  return error instanceof AdminApiError && error.requestId !== null
    ? `Request id ${error.requestId}.`
    : null;
}

/**
 * The bank as a person reads it. The name comes from the import file or the
 * bank, so it is untrusted: a bidi override or line separator must not
 * reorder or split the sentence it sits in (rule 20). Display only; matching
 * a booking to a bank compares the raw fields (sameCoverageBank).
 */
export function bankAccountLabel(bank: Pick<PriorCoverageBank, "bankName" | "bankLast4">): string {
  const name = bank.bankName ? amVisibleDisplayText(bank.bankName) : "";
  const last4 = bank.bankLast4 ? amVisibleDisplayText(bank.bankLast4) : "";
  if (name && last4) return `${name} (${last4})`;
  if (name) return name;
  if (last4) return last4;
  return "this bank";
}

/** `2026-10-07` -> `Oct 7, 2026`; anything unreadable is shown unchanged. */
function formatDisplayDate(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (match === null) return value;
  const date = new Date(`${match[1] ?? ""}-${match[2] ?? ""}-${match[3] ?? ""}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

/**
 * Coverage sentences. Null pull state says "not known yet" and never claims the
 * history is finished. A later earliest date than books start is the gap.
 *
 * The second parameter is the reader's role, kept because every caller passes
 * it and a later lane may need it: the gap sentence is deliberately the SAME
 * for both roles (decision 2's rule — no console control fills a gap in the
 * bank's own history, so neither role is told to ask for one).
 */
export function coverageCopy(
  bank: PriorCoverageBank,
  role: StaffRole = "account_manager",
): CoverageLine[] {
  void role;
  const account = bankAccountLabel(bank);
  const lines: CoverageLine[] = [];
  const earliest = bank.earliestPostedDate;
  const booksStart = bank.booksStart;
  if (earliest !== null) {
    lines.push({
      tone: "info",
      text: `Bank history reaches back to ${formatDisplayDate(earliest)} for ${account}.`,
    });
  } else {
    lines.push({
      tone: "info",
      text: `No posted date is on file yet for ${account}.`,
    });
  }
  if (booksStart !== null && earliest !== null && earliest > booksStart) {
    lines.push({
      tone: "gap",
      // The gap is in the bank's own history: no console control fills it, for
      // either role (round 12, decision 2's rule). So both roles read the fact.
      text: `Transactions between ${formatDisplayDate(booksStart)} and ${formatDisplayDate(
        earliest,
      )} are not in the bank feed.`,
    });
  }
  if (bank.historicalPullComplete === null) {
    lines.push({
      tone: "info",
      text: `Bank history for ${account} is not known yet.`,
    });
  } else if (!bank.historicalPullComplete) {
    lines.push({
      tone: "info",
      text: `Bank history for ${account} is still arriving.`,
    });
  }
  return lines;
}

export function isOlderThanDays(txnDate: string, now: Date, days: number): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(txnDate);
  if (match === null) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
    return false;
  }
  const txnUtc = Date.UTC(year, month - 1, day);
  const check = new Date(txnUtc);
  if (
    check.getUTCFullYear() !== year ||
    check.getUTCMonth() !== month - 1 ||
    check.getUTCDate() !== day
  ) {
    return false;
  }
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return (today - txnUtc) / 86_400_000 > days;
}

export function sameCoverageBank(booking: PriorBooking, bank: PriorCoverageBank): boolean {
  if (booking.bankLast4 !== null && bank.bankLast4 !== null) {
    return booking.bankLast4 === bank.bankLast4;
  }
  if (booking.bankName !== null && bank.bankName !== null) {
    return booking.bankName === bank.bankName;
  }
  return false;
}

export function bankHistoryKnownFinished(
  booking: PriorBooking,
  banks: readonly PriorCoverageBank[],
): boolean {
  for (const bank of banks) {
    if (sameCoverageBank(booking, bank)) return bank.historicalPullComplete === true;
  }
  return false;
}

export function isNotFoundInBankFeed(
  booking: PriorBooking,
  banks: readonly PriorCoverageBank[],
  now: Date,
): boolean {
  return (
    booking.status === "unmatched" &&
    isOlderThanDays(booking.txnDate, now, NOT_FOUND_AFTER_DAYS) &&
    bankHistoryKnownFinished(booking, banks)
  );
}

export function isChangedAfterBooking(booking: PriorBooking): boolean {
  return booking.statusReason === "changed_after_booking";
}

export function bookingInFilter(booking: PriorBooking, filter: BookingFilter): boolean {
  const changed = isChangedAfterBooking(booking);
  switch (filter) {
    case "all":
      return true;
    case "changed-after-booking":
      return changed;
    case "matched-booked":
      return !changed && (booking.status === "matched" || booking.status === "booked");
    case "queued":
      return booking.status === "queued";
    case "ambiguous":
      return booking.status === "ambiguous";
    case "unmatched":
      return booking.status === "unmatched";
    case "excluded":
      return booking.status === "excluded";
    default: {
      const never: never = filter;
      return never;
    }
  }
}

export function bookingStatusLabel(booking: PriorBooking, notFound: boolean): string {
  if (isChangedAfterBooking(booking)) return "Changed after booking";
  if (notFound) return "Not found in bank feed";
  switch (booking.status) {
    case "matched":
      return "Matched";
    case "booked":
      return "Booked";
    case "queued":
      return "Queued";
    case "ambiguous":
      return "Ambiguous";
    case "unmatched":
      return "Unmatched";
    case "excluded":
      return "Excluded";
    default: {
      const never: never = booking.status;
      return never;
    }
  }
}

export function mapRowIsUnmapped(
  row: PriorAccountMapRow,
  openAccounts: readonly string[],
  locations: readonly string[],
): boolean {
  if (row.booksValue === null || row.booksValue === "") return true;
  if (row.kind === "account") return !openAccounts.includes(row.booksValue);
  if (row.kind === "location") return !locations.includes(row.booksValue);
  return !/^[0-9]{4}$/.test(row.booksValue);
}

export function sortMapRows(
  rows: readonly PriorAccountMapRow[],
  openAccounts: readonly string[],
  locations: readonly string[],
): PriorAccountMapRow[] {
  return [...rows].sort((left, right) => {
    const leftUnmapped = mapRowIsUnmapped(left, openAccounts, locations) ? 0 : 1;
    const rightUnmapped = mapRowIsUnmapped(right, openAccounts, locations) ? 0 : 1;
    if (leftUnmapped !== rightUnmapped) return leftUnmapped - rightUnmapped;
    if (left.kind !== right.kind) return left.kind < right.kind ? -1 : 1;
    if (left.externalName < right.externalName) return -1;
    if (left.externalName > right.externalName) return 1;
    return 0;
  });
}

export function formatBookingAmount(cents: number): string {
  if (!Number.isSafeInteger(cents)) return String(cents);
  return formatCents(BigInt(cents));
}

/** CSV of bookings the bank feed does not contain. No memo column. */
export function notFoundCsv(bookings: readonly PriorBooking[]): string {
  const header = "date,amount,payee,bank_name,bank_last4,ext_ref,accounts";
  const lines = bookings.map((booking) => {
    const accounts = booking.lines.map((line) => line.extAccount).join("; ");
    return [
      booking.txnDate,
      formatBookingAmount(booking.amountCents),
      booking.payee ?? "",
      booking.bankName ?? "",
      booking.bankLast4 ?? "",
      booking.extRef,
      accounts,
    ]
      .map(csvField)
      .join(",");
  });
  return [header, ...lines].join("\n");
}

export function importStatusLabel(status: PriorImportStatus): string {
  switch (status) {
    case "uploaded":
      return "Uploaded";
    case "parsing":
      return "Parsing";
    case "parsed":
      return "Parsed";
    case "failed":
      return "Failed";
    default: {
      const never: never = status;
      return never;
    }
  }
}

function isMapKind(value: string | null): value is PriorMapKind {
  return value === "account" || value === "location" || value === "bank";
}

/**
 * The write fingerprint for this page's two writes (B2b: a slug inside a
 * template is what the brand guard cannot tell from a leak). It is an internal
 * key, never words: `runWrite` holds one flight per company and kind, and
 * `forgetPossiblySent` clears it. One place turns the slug into a key.
 */
function writeKey(kind: "map" | "upload", company: CompanySlug): string {
  return `prior-${kind}:${company}`;
}

function defaultSleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

export function renderPriorImportPage(opts: PriorImportPageOptions): void {
  const { client, company, root } = opts;
  const stillCurrent = (): boolean => opts.isCurrent?.() ?? true;
  const now = (): Date => opts.now?.() ?? new Date();
  const pollIntervalMs = opts.pollIntervalMs ?? DEFAULT_POLL_MS;
  const pollMaxMs = opts.pollMaxMs ?? DEFAULT_POLL_MAX_MS;
  const sleep = opts.sleep ?? defaultSleep;
  // The page's ONE label context, built once from the shell's names (rd13 L1).
  const companies = face.accountLabelContext(opts.companyLabels, opts.companySegments);
  clear(root);
  root.removeAttribute("data-prior-import-ready");

  const live = h("div", {
    class: "aria-live",
    "aria-live": "polite",
    "data-role": "prior-import-live",
  });
  root.append(
    h("header", { class: "page-header" }, [
      h("h1", { class: "page-title", "data-role": "prior-import-title" }, [
        navLabel("prior-import"),
      ]),
      h("p", { class: "lede", "data-role": "prior-import-lede" }, [
        "Upload a CSV or Excel file of bookings already made in other software since the books started. One file per upload.",
      ]),
    ]),
    live,
  );

  if (company === "") {
    root.append(
      h("p", { class: "empty-state", "data-role": "prior-import-no-company" }, [
        "Choose a company first. Use the company menu at the top, then come back to this page.",
      ]),
    );
    return;
  }

  const coverageHost = h("section", { "data-role": "coverage" });
  const importsHost = h("section", { "data-role": "prior-imports" });
  const mapHost = h("section", { "data-role": "prior-map" });
  const bookingsHost = h("section", { "data-role": "prior-bookings" });
  // An empty bar at 0% is furniture, not information: both stay out of the
  // layout until an upload has something to report.
  const progress = h("progress", {
    max: "1",
    value: "0",
    "data-role": "upload-progress",
    hidden: "true",
  });
  const progressLabel = h("p", {
    class: "status",
    role: "status",
    "data-role": "upload-progress-label",
    hidden: "true",
  });
  const formatSelect = h("select", {
    "aria-label": "Import format",
    "data-role": "prior-import-format",
  }, [
    h("option", { value: "" }, ["Choose a format"]),
    h("option", { value: "gv-template" }, ["GV template"]),
    h("option", { value: "wave" }, ["Wave"]),
    h("option", { value: "buildium" }, ["Buildium"]),
  ]);
  const fileInput = h("input", {
    type: "file",
    accept: ".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "aria-label": "Prior bookings file",
    "data-role": "prior-import-file",
  });
  const uploadButton = h("button", {
    type: "button",
    class: "btn btn-primary",
    "data-action": "upload-prior-import",
  }, ["Upload"]);
  const uploadHost = h("section", { class: "statements-upload card", "data-role": "prior-import-upload" }, [
      h("label", { class: "file-label field" }, [
        h("span", { class: "field-label" }, ["Format"]),
        formatSelect,
      ]),
      h("label", { class: "file-label field" }, [
        h("span", { class: "field-label" }, ["CSV or Excel file"]),
        fileInput,
      ]),
    uploadButton,
    progress,
    progressLabel,
  ]);
  root.append(
    coverageHost,
    uploadHost,
    importsHost,
    mapHost,
    bookingsHost,
  );

  let loaded: Loaded | null = null;
  let filter: BookingFilter = "all";
  let paintGeneration = 0;
  let csvUrl: string | null = null;

  const paintLiveMessage = (
    message: string,
    retry: (() => void) | null,
    detail: string | null = null,
  ): void => {
    clear(live);
    live.append(document.createTextNode(message));
    if (detail !== null) {
      live.append(
        h("details", { class: "disclosure", "data-role": "show-details" }, [
          h("summary", {}, ["Show details"]),
          h("p", {}, [detail]),
        ]),
      );
    }
    if (retry !== null) {
      const button = h(
        "button",
        { type: "button", class: "btn btn-secondary", "data-action": "retry" },
        ["Try again"],
      );
      button.addEventListener("click", () => {
        retry();
      });
      live.append(button);
    }
  };

  const role: StaffRole = readerRole(opts.role);

  const setProgress = (value: number, label: string): void => {
    const show = label !== "";
    progress.hidden = !show;
    progressLabel.hidden = !show;
    if (show) {
      progress.removeAttribute("hidden");
      progressLabel.removeAttribute("hidden");
    } else {
      progress.setAttribute("hidden", "true");
      progressLabel.setAttribute("hidden", "true");
    }
    progress.value = value;
    progressLabel.textContent = label;
  };

  const paintUploadFailure = (error: unknown): void => {
    setProgress(0, "");
    const copy = failureCopy(error, { role, context: "write" });
    clear(live);
    live.append(document.createTextNode(copy.sentence));
    // The disclosure carries the server's scrubbed words AND the request id a
    // person quotes to support.
    const detail = priorImportFailureDetail(error) ?? copy.detail;
    if (detail !== null) {
      live.append(disclosure("Show details", h("p", {}, [detail])));
    }
  };

  const paintLoadFailure = (error: unknown): void => {
    clear(live);
    live.className = "error-state";
    live.setAttribute("role", "alert");
    live.append(document.createTextNode("Couldn't load prior bookings. Try again."));
    const detail = priorImportFailureDetail(error);
    if (detail !== null) {
      live.append(disclosure("Show details", h("p", {}, [detail])));
    }
    const retry = h(
      "button",
      { type: "button", class: "btn btn-secondary", "data-action": "retry" },
      ["Try again"],
    );
    retry.addEventListener("click", () => {
      void asAnnouncedLoad();
    });
    live.append(retry);
  };

  const asAnnouncedLoad = async (): Promise<boolean> => {
    try {
      clear(live);
      live.className = "aria-live";
      live.setAttribute("role", "status");
      await reload();
      return true;
    } catch (error) {
      if (!stillCurrent()) return false;
      paintLoadFailure(error);
      return false;
    }
  };

  const paintCoverage = (coverage: PriorCoverage): void => {
    clear(coverageHost);
    coverageHost.append(h("h2", { class: "section-title" }, ["Bank history"]));
    if (coverage.banks.length === 0) {
      // A coverage list with no banks is not the same as no bank connection:
      // a connected bank has no prior history until its first download lands
      // (round 1 review, MEDIUM 4). The shell's last health reading says which
      // state this is; with no reading, do not claim either. `bankConnection`
      // decides "is there a bank"; the item list is read for the bank's NAME.
      //
      // Round 2 review, M4: the item list must not decide "no bank" either. A
      // company missing from the reading is `never_read`, which falls through
      // to "Bank history appears after the bank's first download." — the
      // brief's rule for a company with no reading.
      const reading = lastHealth();
      const healthRow = reading?.companies.find((entry) => entry.company === company);
      const items = healthRow?.plaidItems ?? [];
      const bank = bankConnection(healthRow);
      let emptyCopy: string;
      if (bank === "none") {
        emptyCopy = sentence("no_bank_connected", role);
      } else if (items.length > 0) {
        // Only a healthy connection can deliver the first download by itself.
        // A connection needing a sign-in will never download until someone
        // signs in, so "come back once it has" is false for it (round 2
        // review, MEDIUM 3). Mixed fleets read as needing the sign-in: the
        // sentence that promises an arrival must not be told over a bank that
        // cannot arrive. Naming the bank that is holding it matters most in a
        // mixed set: "This bank" leaves the person guessing which one (round 3
        // review, SHOULD-FIX 4).
        const unhealthy = items.filter((item) => item.status !== "healthy");
        emptyCopy =
          unhealthy.length === 0
            ? "A bank is connected, but its first download hasn't arrived yet. Come back once it has."
            : firstDownloadHeldCopy(unhealthy);
      } else {
        emptyCopy = "Bank history appears after the bank's first download.";
      }
      coverageHost.append(
        h("p", { class: "muted", "data-role": "coverage-empty" }, [emptyCopy]),
      );
      return;
    }
    for (const bankCoverage of coverage.banks) {
      for (const line of coverageCopy(bankCoverage, role)) {
        coverageHost.append(
          h("p", {
            class: line.tone === "gap" ? "banner banner-err" : "banner",
            role: "status",
            "data-role": line.tone === "gap" ? "coverage-gap" : "coverage-line",
          }, [line.text]),
        );
      }
    }
  };

  const paintImports = (imports: readonly PriorImportRecord[], truncated: boolean): void => {
    clear(importsHost);
    importsHost.append(h("h2", { class: "section-title" }, ["Uploads"]));
    if (imports.length === 0) {
      importsHost.append(
        h("p", { class: "muted", "data-role": "imports-empty" }, [
          "No files from the other software yet. Choose a format and a file, then press Upload.",
        ]),
      );
      return;
    }
    const list = h("ul", { "data-role": "import-list" });
    for (const row of imports) {
      const reason = row.status === "failed" ? priorImportErrorCopy(row.errorCode) : "";
      list.append(
        h("li", { "data-role": "import-row", "data-import-id": row.id }, [
          `${importStatusLabel(row.status)} · ${row.format} · ${row.fileKind}`,
          reason === "" ? "" : ` — ${reason}`,
        ]),
      );
    }
    importsHost.append(list);
    if (truncated) {
      importsHost.append(
        h("p", { class: "muted", "data-role": "imports-truncated" }, [
          "Some uploads are not listed here.",
        ]),
      );
    }
  };

  const paintMap = (data: Loaded): void => {
    clear(mapHost);
    mapHost.append(h("h2", { class: "section-title" }, ["Mappings"]));
    if (data.openAccounts.length === 0) {
      mapHost.append(
        h("p", { class: "muted", "data-role": "open-accounts-empty" }, [
          "No accounts are ready to map yet. Open this page again after the books have been updated.",
        ]),
      );
    }
    if (data.locationsKnown && data.locations.length === 0) {
      mapHost.append(
        h("p", { class: "muted", "data-role": "locations-empty" }, [
          "No locations are on file yet. You can still map accounts, then add locations before you save those rows.",
        ]),
      );
    }
    const table = h("table", { class: "data-table", "data-role": "map-table" });
    table.append(
      h("thead", {}, [
        h("tr", {}, [
          h("th", {}, ["Kind"]),
          h("th", {}, ["Imported name"]),
          h("th", {}, ["Bookings"]),
          h("th", {}, ["Maps to"]),
        ]),
      ]),
    );
    const body = h("tbody", {});
    const rows = sortMapRows(data.map.rows, data.openAccounts, data.locations);
    for (const row of rows) {
      body.append(renderMapRow(row, data.openAccounts, data.locations, companies));
    }
    table.append(body);
    const save = h("button", {
      type: "button",
      class: "btn btn-primary",
      "data-action": "save-prior-map",
    }, ["Save mappings"]);
    save.addEventListener("click", () => {
      void saveMappings(table);
    });
    mapHost.append(h("div", { class: "table-scroll" }, [table]), save);
  };

  /**
   * One press, one save (rd round 10): `runWrite` holds one flight per key, so a
   * second click, an Enter press or a shortcut cannot send twice. The local
   * check is the `gate`, so a row with nothing chosen is refused in words
   * instead of being reported as a save that may have been lost.
   */
  async function saveMappings(table: HTMLTableElement): Promise<void> {
    let mappings: ReturnType<typeof readMappings> = [];
    const outcome = await runWrite({
      key: writeKey("map", company),
      scope: mapHost,
      gate: () => {
        try {
          mappings = readMappings(table);
          return null;
        } catch (error) {
          return failureCopy(error, { role, context: "write" }).sentence;
        }
      },
      send: () => client.putPriorAccountMap(company, { mappings }),
      afterLanded: async () => {
        // The save is committed. A refresh failure after it is not a failed
        // save, and re-saving is not the remedy (rd R11 R6-18).
        let refreshFailed = false;
        try {
          await reload();
        } catch {
          refreshFailed = true;
        }
        if (!stillCurrent()) return;
        // The page was repainted, so the button that had focus is gone. Say the
        // result once, where it is announced, and put focus on the new button.
        clear(live);
        live.append(
          document.createTextNode(
            refreshFailed
              ? "Mappings saved. The page could not refresh. Reload to see the latest."
              : "Mappings saved.",
          ),
        );
        focusAfterWrite(mapHost.querySelector<HTMLElement>('[data-action="save-prior-map"]'));
      },
      stillCurrent,
      live,
    });
    if (outcome.kind === "landed" || !stillCurrent()) return;
    if (outcome.kind === "refused" && outcome.status === 0) return;
    if (outcome.error !== undefined) {
      // round 11, R7-01: a 422 keeps this page's authored sentence — it names
      // the one thing the person can fix, which no status table can.
      const message =
        outcome.kind === "refused" && outcome.status === 422
          ? priorImportApiFailure(outcome.error, role)
          : failureCopy(outcome.error, { role, context: "write" }).sentence;
      paintLiveMessage(message, () => {
        void saveMappings(table);
      }, priorImportFailureDetail(outcome.error));
      return;
    }
    paintLiveMessage(outcomeSentence(outcome, role), null);
  }

  const paintBookings = (): void => {
    clear(bookingsHost);
    bookingsHost.append(h("h2", { class: "section-title" }, ["Bookings"]));
    const data = loaded;
    if (data === null) return;
    const bar = h("div", { class: "toolbar", "data-role": "booking-filters" });
    for (const item of FILTERS) {
      const pressed = item.id === filter;
      const button = h("button", {
        type: "button",
        class: "btn",
        "data-filter": item.id,
        "aria-pressed": pressed ? "true" : "false",
      }, [item.label]);
      button.addEventListener("click", () => {
        filter = item.id;
        paintBookings();
      });
      bar.append(button);
    }
    bookingsHost.append(bar);
    const ambiguousCount = data.bookings.filter((booking) => booking.status === "ambiguous").length;
    if (ambiguousCount > 0) {
      bookingsHost.append(
        h("p", { class: "banner", role: "status", "data-role": "ambiguous-count" }, [
          `Ambiguous bookings: ${countOrMoreText(ambiguousCount, !data.truncated)}. No matching candidates are stored.`,
        ]),
      );
    }
    const visible = data.bookings.filter((booking) => bookingInFilter(booking, filter));
    if (visible.length === 0) {
      bookingsHost.append(
        h("p", { class: "muted", "data-role": "bookings-empty" }, [
          "No bookings in this filter. Choose All, or upload a file that includes these rows.",
        ]),
      );
    } else {
      const table = h("table", { class: "data-table", "data-role": "bookings-table" });
      table.append(
        h("thead", {}, [
          h("tr", {}, [
            h("th", {}, ["Date"]),
            h("th", {}, ["Amount"]),
            h("th", {}, ["Status"]),
            h("th", {}, ["Payee"]),
            h("th", {}, ["Bank"]),
            h("th", {}, ["Accounts"]),
          ]),
        ]),
      );
      const body = h("tbody", {});
      for (const booking of visible) {
        const notFound = isNotFoundInBankFeed(booking, data.coverage.banks, now());
        const accounts = booking.lines
          .map((line) => amVisibleDisplayText(line.extAccount))
          .join(", ");
        body.append(
          h("tr", { "data-role": "booking-row", "data-booking-id": booking.id }, [
            h("td", {}, [formatDisplayDate(booking.txnDate)]),
            h("td", { class: "num" }, [formatBookingAmount(booking.amountCents)]),
            h("td", { "data-role": "booking-status" }, [bookingStatusLabel(booking, notFound)]),
            h("td", {}, [safeText(booking.payee === null ? null : amVisibleDisplayText(booking.payee))]),
            h("td", {}, [bankAccountLabel(booking)]),
            h("td", {}, [accounts === "" ? "—" : accounts]),
          ]),
        );
      }
      table.append(body);
      bookingsHost.append(h("div", { class: "table-scroll" }, [table]));
    }
    const missing = data.bookings.filter((booking) =>
      isNotFoundInBankFeed(booking, data.coverage.banks, now()),
    );
    if (csvUrl !== null) {
      URL.revokeObjectURL(csvUrl);
      csvUrl = null;
    }
    if (missing.length > 0) {
      const csv = notFoundCsv(missing);
      csvUrl = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
      const href = csvUrl;
      bookingsHost.append(
        h("a", {
          href,
          download: "not-found-in-bank-feed.csv",
          "data-role": "not-found-csv",
        }, ["Download CSV of bookings not found in the bank feed"]),
      );
    }
    if (data.truncated) {
      bookingsHost.append(
        h("p", { class: "muted", "data-role": "bookings-truncated" }, [
          "Some bookings are not listed here.",
        ]),
      );
    }
  };

  const paint = (data: Loaded): void => {
    loaded = data;
    paintCoverage(data.coverage);
    paintImports(data.imports, data.truncated);
    paintMap(data);
    paintBookings();
    root.setAttribute("data-prior-import-ready", "true");
  };

  const reload = async (): Promise<void> => {
    const generation = ++paintGeneration;
    root.removeAttribute("data-prior-import-ready");
    const [coverage, map, openAccountPage, importPage] = await Promise.all([
      client.getPriorCoverage(company),
      client.getPriorAccountMap(company),
      client.listOpenAccounts(company),
      listAllImports(),
    ]);
    if (!stillCurrent() || generation !== paintGeneration) return;
    let locations: string[] = [];
    let locationsKnown = true;
    try {
      const chart = await client.getChartSnapshot(company);
      if (!stillCurrent() || generation !== paintGeneration) return;
      locations =
        chart.selectableLocations.length > 0 ? chart.selectableLocations : chart.locations;
    } catch (error) {
      if (!stillCurrent() || generation !== paintGeneration) return;
      // The read failed, so the list is unknown. Saying "No locations are on
      // file yet" beside the failure is a claim from a failed read (B6).
      locationsKnown = false;
      paintLiveMessage(
        `The location list could not be loaded. ${priorImportApiFailure(error)}`,
        () => {
          void reload();
        },
      );
    }
    const bookings: PriorBooking[] = [];
    let truncated = importPage.truncated;
    for (const row of importPage.imports) {
      const page = await bookingsForImport(row.id);
      if (!stillCurrent() || generation !== paintGeneration) return;
      bookings.push(...page.bookings);
      truncated = truncated || page.truncated;
    }
    paint({
      coverage,
      map,
      openAccounts: openAccountPage.accounts.map((row: OpenAccountRow) => row.account),
      locations,
      locationsKnown,
      imports: importPage.imports,
      bookings: dedupeBookings(bookings),
      truncated,
    });
  };

  async function listAllImports(): Promise<{
    imports: PriorImportRecord[];
    truncated: boolean;
  }> {
    const imports: PriorImportRecord[] = [];
    let cursor: string | undefined;
    const seen = new Set<string>();
    for (let page = 0; page < MAX_PAGES; page += 1) {
      const result = await client.listPriorImports(
        company,
        cursor === undefined ? {} : { cursor },
      );
      imports.push(...result.imports);
      if (result.nextCursor === null) return { imports, truncated: false };
      if (seen.has(result.nextCursor)) return { imports, truncated: true };
      seen.add(result.nextCursor);
      cursor = result.nextCursor;
    }
    return { imports, truncated: true };
  }

  async function bookingsForImport(id: string): Promise<{
    bookings: PriorBooking[];
    truncated: boolean;
  }> {
    const bookings: PriorBooking[] = [];
    let cursor: string | undefined;
    const seen = new Set<string>();
    for (let page = 0; page < MAX_PAGES; page += 1) {
      const result =
        cursor === undefined
          ? await client.getPriorImport(company, id)
          : await client.getPriorImport(company, id, cursor);
      bookings.push(...result.bookings);
      if (result.nextCursor === null) return { bookings, truncated: false };
      if (seen.has(result.nextCursor)) return { bookings, truncated: true };
      seen.add(result.nextCursor);
      cursor = result.nextCursor;
    }
    return { bookings, truncated: true };
  }

  uploadButton.addEventListener("click", () => {
    void upload();
  });

  /**
   * One press, one upload. The local checks are the `gate` (so a bad file is
   * refused in words and nothing is sent), the mint and the upload are `send`,
   * and the parse's result and the re-read are `afterLanded`: an upload that
   * landed is never reported as "did not save", and a parse the Worker refused
   * is not a failed save either.
   *
   * `blockWhenPossiblySent`: a lost answer may already have uploaded this file,
   * so a second press offers the adopt-or-forget path (`Check for it` reads the
   * list, then forgets the key) instead of sending twice.
   */
  async function upload(): Promise<void> {
    const file = fileInput.files?.[0];
    let chosenFormat: PriorImportFormat | null = null;
    let kind: "csv" | "xlsx" | null = null;
    clear(live);
    const outcome = await runWrite({
      key: writeKey("upload", company),
      scope: uploadHost,
      gate: () => {
        if (file === undefined) return "Choose a CSV or XLSX file.";
        const format = formatSelect.value;
        const refusal = uploadRefusal(file, format);
        if (refusal !== null) return refusal;
        if (!isPriorImportFormat(format)) {
          return "Choose Wave, Buildium, or the GV template before uploading.";
        }
        chosenFormat = format;
        kind = fileKindOfName(file.name);
        return kind === null ? "Only a CSV or XLSX file can be uploaded." : null;
      },
      send: async () => {
        const chosen = file as File;
        const fileKind = kind as "csv" | "xlsx";
        const format = chosenFormat as PriorImportFormat;
        setProgress(0.2, "Hashing the file…");
        const fileSha256 = await sha256HexOfBlob(chosen);
        if (!stillCurrent()) return null;
        setProgress(0.55, "Uploading…");
        const minted = await client.mintPriorImportUploadUrl(company, {
          fileKind,
          format,
          fileSha256,
        });
        await client.putPriorImportFile(minted.uploadUrl, chosen);
        if (!stillCurrent()) return null;
        return pollImport(minted.importId);
      },
      afterLanded: async (detail) => {
        if (!stillCurrent() || detail === null) return;
        if (detail.import.status === "failed") {
          // The file uploaded; the Worker could not parse it. That is the
          // server's answer about the file, not a save that was lost.
          setProgress(0, "");
          paintLiveMessage(priorImportErrorCopy(detail.import.errorCode), null);
          return;
        }
        setProgress(1, importStatusLabel(detail.import.status));
        // The upload and parse are committed. A refresh failure after them is
        // not a failed upload and re-uploading is not the remedy (R6-18).
        let refreshFailed = false;
        try {
          await reload();
        } catch {
          refreshFailed = true;
        }
        if (!stillCurrent()) return;
        if (refreshFailed) {
          clear(live);
          live.className = "aria-live";
          live.setAttribute("role", "status");
          live.append(
            document.createTextNode(
              "The file was uploaded. The page could not refresh. Reload to see the upload.",
            ),
          );
        }
      },
      blockWhenPossiblySent: true,
      stillCurrent,
      live,
    });
    if (!stillCurrent() || outcome.kind === "landed") return;
    if (outcome.kind === "refused" && outcome.status === 0) return;
    if (outcome.kind === "unknown" && outcome.blocked === true) {
      paintBlockedUpload();
      return;
    }
    if (outcome.error !== undefined) {
      paintUploadFailure(outcome.error);
      return;
    }
    paintLiveMessage(outcomeSentence(outcome, role), null);
  }

  /**
   * The adopt-or-forget path for an upload whose answer was lost: read the
   * list (the upload shows there if it landed), then forget the key so the
   * person can upload again if it did not.
   *
   * Round 2 review, M2: the key is forgotten only AFTER the reload answers. A
   * failed reload proves nothing about the lost upload, so clearing the mark
   * then would let the next press send a second copy of a file that may have
   * landed (the LOW in the round-1 review).
   */
  function paintBlockedUpload(): void {
    setProgress(0, "");
    clear(live);
    live.append(
      document.createTextNode(
        outcomeSentence({ kind: "unknown", blocked: true }, role),
      ),
    );
    const check = h(
      "button",
      { type: "button", class: "btn btn-secondary", "data-action": "check-upload" },
      ["Check for it"],
    );
    check.addEventListener("click", () => {
      void (async () => {
        try {
          const answered = await asAnnouncedLoad();
          if (answered) forgetPossiblySent(writeKey("upload", company));
        } catch (error) {
          if (stillCurrent()) paintLoadFailure(error);
        }
      })();
    });
    live.append(check);
  }

  async function pollImport(importId: string): Promise<PriorImportDetail | null> {
    try {
      return await pollImportDetail(importId);
    } finally {
      // The parse has settled (or this page stopped watching): what it
      // changed must not be served from the read cache (rd-x2).
      settleReads(client);
    }
  }

  async function pollImportDetail(importId: string): Promise<PriorImportDetail | null> {
    const started = Date.now();
    let detail = await client.getPriorImport(company, importId);
    while (detail.import.status === "uploaded" || detail.import.status === "parsing") {
      setProgress(
        detail.import.status === "parsing" ? 0.85 : 0.7,
        importStatusLabel(detail.import.status),
      );
      if (Date.now() - started >= pollMaxMs) {
        throw new Error(
          "The file is uploaded, and the parse has not finished. Refresh to check again.",
        );
      }
      await sleep(pollIntervalMs);
      if (!stillCurrent()) return null;
      detail = await client.getPriorImport(company, importId);
    }
    return detail;
  }

  void runAnnouncedAction(live, stillCurrent, "Load", async () => {
    await asAnnouncedLoad();
  });
}

function renderMapRow(
  row: PriorAccountMapRow,
  openAccounts: readonly string[],
  locations: readonly string[],
  companies: AccountLabelContext,
): HTMLTableRowElement {
  const saved = row.booksValue;
  let control: HTMLElement;
  if (row.kind === "bank") {
    const input = h("input", {
      type: "text",
      class: "input",
      maxlength: "4",
      "aria-label": `Bank last 4 for ${amVisibleDisplayText(row.externalName)}`,
      "data-role": "map-value",
      placeholder: "Last 4",
      autocomplete: "off",
    });
    if (saved !== null && /^[0-9]{4}$/.test(saved)) input.value = saved;
    control = input;
  } else {
    const choices = row.kind === "account" ? openAccounts : locations;
    // An account reads as words, never as `Assets:Wallet:Operating`; twins that
    // would read the same are told apart by the shared label set (rd round 10).
    // The VALUE stays the stored path — only the text changes.
    const savedMissing =
      saved !== null && saved !== "" && !choices.includes(saved);
    const labelAccounts =
      row.kind === "account"
        ? savedMissing
          ? [...openAccounts, saved]
          : openAccounts
        : null;
    const accountLabels =
        labelAccounts === null ? null : accountLabelSet(labelAccounts, companies);
    const select = h("select", {
      "aria-label": `Map ${amVisibleDisplayText(row.externalName)}`,
      "data-role": "map-value",
    }, [h("option", { value: "" }, ["Choose…"])]);
    if (savedMissing) {
      // A mapping the Worker still applies is not "Choose…": it is saved, and
      // re-picking nothing would misdescribe what drives matching (R6-19).
      const text = accountLabels?.get(saved) ?? amVisibleDisplayText(saved);
      select.append(h("option", { value: saved }, [`${text} (no longer available)`]));
    }
    for (const name of choices) {
      const text = accountLabels?.get(name) ?? amVisibleDisplayText(name);
      select.append(h("option", { value: name }, [text]));
    }
    if (saved !== null && (choices.includes(saved) || savedMissing)) select.value = saved;
    control = select;
  }
  return h("tr", {
    "data-kind": row.kind,
    "data-external-name": row.externalName,
    "data-role": "map-row",
  }, [
    h("td", {}, [row.kind === "account" ? "Account" : row.kind === "location" ? "Location" : "Bank"]),
    h("td", {}, [amVisibleDisplayText(row.externalName)]),
    h("td", {}, [String(row.bookingCount)]),
    h("td", {}, [control]),
  ]);
}

function readMappings(table: HTMLTableElement): Array<{
  kind: PriorMapKind;
  externalName: string;
  booksValue: string;
}> {
  const mappings: Array<{ kind: PriorMapKind; externalName: string; booksValue: string }> = [];
  for (const row of table.querySelectorAll("tr[data-kind]")) {
    const kindRaw = row.getAttribute("data-kind");
    const externalName = row.getAttribute("data-external-name");
    if (!isMapKind(kindRaw) || externalName === null || externalName === "") continue;
    const control = row.querySelector("[data-role=map-value]");
    if (!(control instanceof HTMLSelectElement) && !(control instanceof HTMLInputElement)) {
      continue;
    }
    const booksValue = control.value.trim();
    if (booksValue === "") continue;
    if (kindRaw === "bank" && !/^[0-9]{4}$/.test(booksValue)) {
      throw new AuthoredRefusal("Bank mappings use the last 4 digits only.");
    }
    mappings.push({ kind: kindRaw, externalName, booksValue });
  }
  if (mappings.length === 0) {
    throw new AuthoredRefusal(
      "Choose a GV account, a location, or a bank last 4 before saving.",
    );
  }
  return mappings;
}

function dedupeBookings(bookings: readonly PriorBooking[]): PriorBooking[] {
  const seen = new Set<string>();
  const out: PriorBooking[] = [];
  for (const booking of bookings) {
    if (seen.has(booking.id)) continue;
    seen.add(booking.id);
    out.push(booking);
  }
  return out;
}

/**
 * Which bank is holding the first download, and what it needs.
 *
 * Round 2 review, M4: every unhealthy item reads "needs reconnecting" — the
 * same decided word as Connection status and Bank accounts. `login_required`
 * is a sign-in, and a sign-in IS the reconnect, so the old "needs to be signed
 * in again" spelling is gone. A mixed fleet is named in one list, because both
 * kinds need the same action, with a plain word for a bank whose name the API
 * did not send (rd R11 R6b round 3 review, SHOULD-FIX 4).
 */
function firstDownloadHeldCopy(
  unhealthy: CompanyHealth["plaidItems"],
): string {
  const names = unhealthy.map((item) =>
    safeText(amVisibleDisplayText(item.institution ?? ""), "Bank"),
  );
  const possessive = unhealthy.length === 1 ? "its" : "their";
  return `${bankNameList(names)} ${unhealthy.length === 1 ? "needs" : "need"} reconnecting before ${possessive} first download can arrive.`;
}

/** "A", "A and B", "A, B and C" — read aloud, never "A,B". */
function bankNameList(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? "A bank";
  const head = names.slice(0, -1).join(", ");
  return `${head} and ${names[names.length - 1] ?? ""}`;
}
